import { Rnd } from 'react-rnd';
import { CANVAS_SIZE, toCanvas, toDisplay } from '../lib/canvas-coords';

interface Props {
  label: string;
  x: number; // canvas coords
  y: number;
  width: number; // canvas coords
  height: number;
  displaySize: number; // pixel size of canvas surface
  /** Canvas-space width the drag is clamped to. 1080 for a full canvas; the
   *  product-half width in lifestyle mode, where x/y are half-space absolutes
   *  and dragging past the seam would place the element under the photo. */
  boundsWidth?: number;
  boundsHeight?: number;
  selected?: boolean;
  color?: string;
  /** Corner-drag resize handles; only the product-image box opts in today —
   *  badges and custom overlays keep a fixed size and stay drag-only. */
  resizable?: boolean;
  onSelect?: () => void;
  onChange: (x: number, y: number) => void;
  onResize?: (x: number, y: number, w: number, h: number) => void;
}

export default function DraggableElement({
  label,
  x,
  y,
  width,
  height,
  displaySize,
  boundsWidth = CANVAS_SIZE,
  boundsHeight = CANVAS_SIZE,
  selected,
  color = 'var(--ff-orange)',
  resizable,
  onSelect,
  onChange,
  onResize,
}: Props) {
  const dx = toDisplay(x, displaySize);
  const dy = toDisplay(y, displaySize);
  const dw = toDisplay(width, displaySize);
  const dh = toDisplay(height, displaySize);

  return (
    <Rnd
      position={{ x: dx, y: dy }}
      size={{ width: dw, height: dh }}
      bounds="parent"
      enableResizing={
        resizable
          ? { topLeft: true, topRight: true, bottomLeft: true, bottomRight: true }
          : false
      }
      minWidth={toDisplay(1, displaySize)}
      minHeight={toDisplay(1, displaySize)}
      dragGrid={[2, 2]}
      onDragStop={(_e, d) => {
        const cx = toCanvas(d.x, displaySize);
        const cy = toCanvas(d.y, displaySize);
        onChange(
          Math.max(0, Math.min(Math.max(0, boundsWidth - width), cx)),
          Math.max(0, Math.min(Math.max(0, boundsHeight - height), cy)),
        );
      }}
      onResizeStop={(_e, _dir, ref, _delta, pos) => {
        if (!onResize) return;
        // Spec: the resize write must stay clamped to the canvas / lifestyle
        // half (boundsWidth x boundsHeight) — the same bounds onDragStop
        // clamps position against. react-rnd's bounds="parent" is only the
        // interactive guard for a mouse-driven resize; it enforces nothing
        // about the box handed to onResize, so a future bounds change, a
        // keyboard/programmatic resize, or any other caller must not be able
        // to write x+w / y+h past the edge.
        let w = Math.max(1, Math.trunc(toCanvas(ref.offsetWidth, displaySize)));
        let h = Math.max(1, Math.trunc(toCanvas(ref.offsetHeight, displaySize)));
        let x = Math.max(0, Math.trunc(toCanvas(pos.x, displaySize)));
        let y = Math.max(0, Math.trunc(toCanvas(pos.y, displaySize)));
        x = Math.min(x, Math.max(0, boundsWidth - 1));
        y = Math.min(y, Math.max(0, boundsHeight - 1));
        w = Math.max(1, Math.min(w, boundsWidth - x));
        h = Math.max(1, Math.min(h, boundsHeight - y));
        onResize(x, y, w, h);
      }}
      onMouseDown={onSelect}
      className="group"
    >
      {/* Selected: full frame (fill + ring + corners + label) so the user can
          see what they're editing. Unselected: invisible-but-clickable target
          that surfaces only on hover, so the rendered preview underneath isn't
          obscured by editor chrome (matters most for shape='none'). */}
      <div
        className={`relative h-full w-full rounded-xl cursor-grab active:cursor-grabbing select-none transition-all duration-150 ${
          selected
            ? 'ring-2 ring-offset-1 ring-offset-transparent shadow-lg'
            : 'ring-0 group-hover:ring-1 group-hover:ring-dashed'
        }`}
        style={{
          background: selected ? `color-mix(in srgb, ${color} 13%, transparent)` : 'transparent',
          borderColor: color,
          // @ts-expect-error custom prop for ring color via inline style
          '--tw-ring-color': color,
        }}
      >
        {selected && (
          <>
            <Corner pos="tl" color={color} />
            <Corner pos="tr" color={color} />
            <Corner pos="bl" color={color} />
            <Corner pos="br" color={color} />

            <span
              className="absolute left-1.5 top-1.5 whitespace-nowrap rounded-full px-2 py-1 text-[9px] font-bold uppercase tracking-widish text-white shadow-soft"
              style={{ background: color }}
            >
              {label}
            </span>
          </>
        )}
      </div>
    </Rnd>
  );
}

function Corner({ pos, color }: { pos: 'tl' | 'tr' | 'bl' | 'br'; color: string }) {
  const cls =
    pos === 'tl'
      ? 'top-0 left-0'
      : pos === 'tr'
      ? 'top-0 right-0'
      : pos === 'bl'
      ? 'bottom-0 left-0'
      : 'bottom-0 right-0';
  return (
    <span
      className={`absolute h-2.5 w-2.5 rounded-full ${cls} translate-x-[-50%] translate-y-[-50%] border-2 border-white`}
      style={{ background: color, transform: cornerTx(pos) }}
    />
  );
}

function cornerTx(pos: 'tl' | 'tr' | 'bl' | 'br') {
  if (pos === 'tl') return 'translate(-50%,-50%)';
  if (pos === 'tr') return 'translate(50%,-50%)';
  if (pos === 'bl') return 'translate(-50%,50%)';
  return 'translate(50%,50%)';
}
